import { useEffect, useState } from 'react';
import { Clock, RefreshCw, Timer, Coffee } from 'lucide-react';

interface ServerDownPageProps {
  onRetry: () => void;
  retryCount: number;
}

const funMessages = [
  "⏰ Taking a quick billable break!",
  "⚖️ Calculating time entries with legal precision!",
  "☕ Our timers are on a coffee break!",
  "📝 Filing away those timesheet entries!",
  "🕐 Every minute counts... including this one!",
  "⏱️ Tracking the time it takes to fix this!",
  "📈 Optimizing your time tracking experience!",
];

export const ServerDownPage: React.FC<ServerDownPageProps> = ({ onRetry, retryCount }) => {
  const [messageIndex, setMessageIndex] = useState(0);
  const [dots, setDots] = useState('');

  // Rotate through fun messages
  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % funMessages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Animated dots
  useEffect(() => {
    const interval = setInterval(() => {
      setDots((prev) => (prev.length >= 3 ? '' : prev + '.'));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="max-w-2xl w-full text-center">
        {/* Animated Icon */}
        <div className="mb-8 relative">
          <div className="inline-block">
            <Clock 
              className="w-24 h-24 text-primary dark:text-primary animate-bounce" 
              strokeWidth={1.5}
            />
            <div className="absolute -top-2 -right-2 w-8 h-8 bg-accent rounded-full animate-ping"></div>
          </div>
        </div>

        {/* Main Message */}
        <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-4 animate-fade-in">
          We'll be right back!
        </h1>

        {/* Fun Rotating Message */}
        <div className="h-20 flex items-center justify-center mb-8">
          <p 
            key={messageIndex}
            className="text-xl md:text-2xl text-muted-foreground animate-slide-up"
          >
            {funMessages[messageIndex]}
          </p>
        </div>

        {/* Status Message */}
        <div className="bg-card backdrop-blur-sm rounded-2xl shadow-xl p-8 mb-8 border border-border">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Timer className="w-6 h-6 text-primary animate-wiggle" />
            <h2 className="text-2xl font-semibold text-card-foreground">
              Under Maintenance
            </h2>
          </div>
          
          <p className="text-lg text-muted-foreground mb-6">
            Our team is working hard to get things back up and running{dots}
          </p>

          {/* Auto-retry indicator */}
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground mb-4">
            <RefreshCw className="w-4 h-4 animate-spin" />
            <span>Auto-checking server status...</span>
          </div>

          {/* Manual Retry Button */}
          <button
            onClick={onRetry}
            className="px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95"
          >
            Check Now
          </button>

          {retryCount > 0 && (
            <p className="mt-4 text-sm text-muted-foreground">
              Retry attempt: {retryCount}
            </p>
          )}
        </div>

        {/* Additional Info */}
        <div className="flex items-center justify-center gap-2 text-muted-foreground">
          <Coffee className="w-5 h-5" />
          <p className="text-sm">
            Meanwhile, grab a coffee! Your hours are safe with us.
          </p>
        </div>

        {/* Floating Elements - using theme colors */}
        <div className="fixed top-10 left-10 w-20 h-20 bg-primary/10 rounded-full opacity-50 animate-float"></div>
        <div className="fixed bottom-10 right-10 w-32 h-32 bg-accent/10 rounded-full opacity-50 animate-float-delayed"></div>
        <div className="fixed top-1/2 left-20 w-16 h-16 bg-secondary/10 rounded-full opacity-50 animate-float-slow"></div>
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slide-up {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes wiggle {
          0%, 100% {
            transform: rotate(-3deg);
          }
          50% {
            transform: rotate(3deg);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          50% {
            transform: translateY(-20px) translateX(10px);
          }
        }

        @keyframes float-delayed {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          50% {
            transform: translateY(20px) translateX(-10px);
          }
        }

        @keyframes float-slow {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          50% {
            transform: translateY(-15px) translateX(-15px);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.8s ease-out;
        }

        .animate-slide-up {
          animation: slide-up 0.6s ease-out;
        }

        .animate-wiggle {
          animation: wiggle 1s ease-in-out infinite;
        }

        .animate-float {
          animation: float 6s ease-in-out infinite;
        }

        .animate-float-delayed {
          animation: float-delayed 8s ease-in-out infinite;
        }

        .animate-float-slow {
          animation: float-slow 10s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};
